import {useMountedTruck} from "../driver/mountedTruck.ts";
import {useNavigate} from "@tanstack/react-router";
import {useCallback} from "react";
import {Tripleg, VenueStub} from "../rpc/webrpc-client.gen.ts";
import {useStartTriplegMutation,} from "../tq/tqx.tsx";
import {z} from "zod/v4";
import {BaseTimelineScene} from "../timeline/timeline.tsx";
import {useTranslation} from "react-i18next";
import {SceneTileContent} from "../dispatch/DispatchTile.tsx";
import {VenueCapsule} from "../venue/capsule.tsx";
import {
    faHourglassStart,
    faTruckContainer,
    faTruckContainerEmpty,
    faTruckFast
} from "@fortawesome/pro-regular-svg-icons";
import {makeVenuePlace, VenuePlace} from "../tq/tqxx.tsx";
import {SceneViewProps} from "../timeline/sceneViewProps.tsx";
import {useLoadedBundles} from "../dispatch/useLoadedBundles.tsx";
import {TimelineTimePoint} from "../timeline/timelineTimeCoordinate.tsx";

// import {TimelineCell} from "../timeline/timeline.tsx";

export function useTriplegStarter() {
    const mountedTruckTqrv = useMountedTruck();
    const mountedTruck = mountedTruckTqrv.data;
    const {mutateAsync: startTripleg} = useStartTriplegMutation();
    const navigate = useNavigate();

    const rv = useCallback((destinationId: string) => {
        if (mountedTruck) {
            return startTripleg({destinationId})
                .then(() => navigate({
                    to: "/truck/$id/drive",
                    params: {id: mountedTruck.id},
                }));
        } else {
            return Promise.reject(new Error("no mounted truck"));
        }
    }, [mountedTruck, navigate, startTripleg]);

    return rv;
}

const TriplegSceneBase = z.object({
    ...BaseTimelineScene.shape,
    subjectFlavor: z.literal("tripleg"),
    subject: Tripleg,
    when: TimelineTimePoint,
});

export const TriplegDepartedScene = z.object({
    ...TriplegSceneBase.shape,
    sceneFlavor: z.literal("departed"),
    where: z.optional(VenuePlace),
    destinedFor: z.optional(VenueStub),
});
export type TriplegDepartedScene = z.infer<typeof TriplegDepartedScene>;

const TriplegArrivedScene = z.object({
    ...TriplegSceneBase.shape,
    sceneFlavor: z.literal("arrived"),
    where: VenuePlace,
});
export type TriplegArrivedScene = z.infer<typeof TriplegArrivedScene>;

const TriplegAdmittedScene = z.object({
    ...TriplegSceneBase.shape,
    sceneFlavor: z.literal("admitted"),
    where: VenuePlace,
});
export type TriplegAdmittedScene = z.infer<typeof TriplegAdmittedScene>;

export const TriplegScene =
    z.discriminatedUnion("sceneFlavor", [TriplegDepartedScene, TriplegArrivedScene, TriplegAdmittedScene]);
export type TriplegScene = z.infer<typeof TriplegScene>;

export function getLastTriplegScene(tripleg: Tripleg): TriplegScene {
    const prv = {subjectFlavor: "tripleg", subject: tripleg} as const;
    if (tripleg.admitted) {
        return {
            ...prv,
            sceneFlavor: "admitted",
            where: makeVenuePlace(tripleg.destination),
            when: {at: tripleg.admitted},
        };
    } else if (tripleg.arrived) {
        return {
            ...prv,
            sceneFlavor: "arrived",
            where: makeVenuePlace(tripleg.destination),
            when: {at: tripleg.arrived},
        };
    } else {
        return {
            ...prv,
            sceneFlavor: "departed",
            where: tripleg.origin ? makeVenuePlace(tripleg.origin) : undefined,
            destinedFor: tripleg.destination,
            when: {at: tripleg.departed},
        };
    }
}

export function synthTriplegScenes({triplegs}: { triplegs: Tripleg[] }): TriplegScene[] {
    const rv: TriplegScene[] = [];
    for (const tripleg of triplegs) {
        const departScene: TriplegDepartedScene = {
            subjectFlavor: "tripleg",
            subject: tripleg,
            sceneFlavor: "departed",
            when: {at: tripleg.departed},
            where: tripleg.origin ? makeVenuePlace(tripleg.origin) : undefined,
        };
        rv.push(departScene);

        if (tripleg.arrived) {
            const scene: TriplegArrivedScene = {
                subjectFlavor: "tripleg",
                subject: tripleg,
                sceneFlavor: "arrived",
                when: {at: tripleg.arrived},
                where: makeVenuePlace(tripleg.destination),
            };
            rv.push(scene);
        }

        if (tripleg.admitted) {
            const scene: TriplegAdmittedScene = {
                subjectFlavor: "tripleg",
                subject: tripleg,
                sceneFlavor: "admitted",
                when: {at: tripleg.admitted},
                where: makeVenuePlace(tripleg.destination),
            };
            rv.push(scene);
        }
    }

    return rv;
}

const triplegSceneColors = {
    departed: "success",
    arrived: "warning",
    admitted: "primary",
} as const;

export function useTriplegSceneColor(scene?: TriplegScene) {
    return scene ? triplegSceneColors[scene.sceneFlavor] : undefined;
}

// const TriplegSceneDecoratorArgsSchema = makeSceneDecoratorArgsSchema(TriplegSceneSchema);
// type TriplegSceneDecoratorArgs = z.infer<typeof TriplegSceneDecoratorArgsSchema>;
//

export function TriplegSceneTileContent({scene: inScene, color: inColor, variant}: SceneViewProps<TriplegScene>) {
    const {t} = useTranslation();
    const sceneZprv = TriplegScene.safeParse(inScene);
    const scene = sceneZprv.data;
    const tripleg = scene?.subject;

    const intrinsicColor = useTriplegSceneColor(scene);
    const color = inColor ?? intrinsicColor;

    const loadedBundleTqrv = useLoadedBundles(tripleg && {truckId: tripleg.truck.id});
    const loadedBundles = loadedBundleTqrv.data;
    const direction = !!loadedBundles
    && loadedBundles.some(maybe => maybe.destination && maybe.destination.id === tripleg?.destination.id)
        ? "homebound" : "outbound";

    if (!sceneZprv.success || !tripleg) {
        return null;
    }

    switch (scene.sceneFlavor) {
        case "departed":
            return <SceneTileContent
                what={t("driver.departed")}
                when={{at: tripleg.departed}}
                where={!!tripleg.origin && <VenueCapsule venue={tripleg.origin}/>}
                icon={{
                    icon: faTruckFast,
                    flip: direction === "homebound" ? "horizontal" : undefined
                }}
                color={color} variant={variant}
            />;

        case "arrived":
            return <SceneTileContent
                what={t("driver.arrived")}
                when={{at: tripleg.arrived!}}
                where={<VenueCapsule venue={tripleg.destination}/>}
                icon={{icon: faHourglassStart}}
                color={color} variant={variant}
            />;

        case "admitted":
            return <SceneTileContent
                what={t("driver.admitted")}
                when={{at: tripleg.admitted!}}
                where={<VenueCapsule venue={tripleg.destination}/>}
                icon={{icon: (loadedBundles?.length ?? 0) > 0 ? faTruckContainer : faTruckContainerEmpty}}
                color={color} variant={variant}
            />;

        default:
            return null;
    }
}

